import {
  LiveVoiceFunctionCalls,
  type LiveVoiceFunctionCall,
} from './live-voice-function-calls';
import { LiveVoiceContextCommands } from './live-voice-context-commands';
import {
  observeLiveVoiceInputActivity,
  observeLiveVoicePlayback,
  type LiveVoicePlayback,
} from './live-voice-playback';
import {
  chunkLiveVoiceContext,
  isLiveVoiceFinalEvent,
  LIVE_VOICE_CONNECT_TIMEOUT_MS,
  LIVE_VOICE_FINISH_TIMEOUT_MS,
  liveVoiceEventId,
  liveVoiceRecord,
  parseLiveVoiceAnswer,
  parseLiveVoiceEvent,
  type LiveVoiceContextKind,
  type LiveVoiceEvent,
  type LiveVoiceFailure,
} from './live-voice-protocol';

export type { LiveVoiceFunctionCall } from './live-voice-function-calls';

export type LiveVoiceSessionOptions = {
  micStream: MediaStream;
  audioElement: HTMLAudioElement;
  isCurrentCall: () => boolean;
  onRemoteStream: (stream: MediaStream) => void;
  onEvent: (event: Record<string, unknown>) => void;
  onPlaybackChange?: (speaking: boolean) => void;
  onInputActivity?: (active: boolean) => void;
  onFailure?: (reason: LiveVoiceFailure) => void;
  onTranscriptFlush?: (finalized?: boolean) => Promise<boolean>;
  executeTool: (call: LiveVoiceFunctionCall) => Promise<string>;
};

type ConnectOptions = { apiKey: string; model: string; url: string };
type State = 'new' | 'connecting' | 'active' | 'finishing' | 'closed';
const MAX_QUEUED_CONTEXT_BYTES = 64 * 1024;

/** A single Live WebRTC session. It never reconnects or retries paid creation. */
export class LiveVoiceSession {
  private state: State = 'new';
  private peer: RTCPeerConnection | null = null;
  private channel: RTCDataChannel | null = null;
  private remoteStream: MediaStream | null = null;
  private playback: LiveVoicePlayback | null = null;
  private inputActivity: { dispose: () => void } | null = null;
  private readonly abort = new AbortController();
  private readonly calls: LiveVoiceFunctionCalls;
  private readonly contextCommands = new LiveVoiceContextCommands();
  private expectedModel = '';
  private providerSessionId: string | null = null;
  private muted = false;
  private finalized = false;
  private mediaStopped = false;
  private resolveStarted: (() => void) | null = null;
  private rejectStarted: (() => void) | null = null;
  private finishPromise: Promise<boolean> | null = null;
  private resolveFinished: ((complete: boolean) => void) | null = null;
  private connectTimer: ReturnType<typeof setTimeout> | undefined;
  private finishTimer: ReturnType<typeof setTimeout> | undefined;
  private disconnectTimer: ReturnType<typeof setTimeout> | undefined;
  private queuedBytes = 0;
  private queuedContext: Array<{
    content: string;
    kind: LiveVoiceContextKind;
  }> = [];

  constructor(private readonly options: LiveVoiceSessionOptions) {
    this.calls = new LiveVoiceFunctionCalls({
      executeTool: options.executeTool,
      isActive: () => this.state === 'active' && options.isCurrentCall(),
      send: (event) => this.send(event),
      onFailure: (reason) => this.fail(reason),
    });
  }

  get sessionId(): string | null {
    return this.providerSessionId;
  }

  get isClosing(): boolean {
    return this.state === 'finishing' || this.state === 'closed';
  }

  /** Latest caption position, for diagnostics only. */
  get currentInputTranscriptEndMs(): number | null {
    return this.calls.currentInputTranscriptEndMs;
  }

  getRejectedContextKind(
    event: Record<string, unknown>
  ): LiveVoiceContextKind | null {
    return this.contextCommands.rejectedKind(event);
  }

  async connect(args: ConnectOptions): Promise<void> {
    if (this.state !== 'new' || !this.options.isCurrentCall()) {
      throw new Error('Live voice connection is no longer available');
    }
    this.state = 'connecting';
    this.expectedModel = args.model;
    const started = new Promise<void>((resolve, reject) => {
      this.resolveStarted = resolve;
      this.rejectStarted = () =>
        reject(new Error('Live voice connection failed'));
    });
    this.connectTimer = setTimeout(
      () => this.fail('connection'),
      LIVE_VOICE_CONNECT_TIMEOUT_MS
    );
    try {
      await Promise.all([this.openConnection(args), started]);
      this.assertCurrent();
    } catch {
      this.close();
      throw new Error('Live voice connection failed');
    } finally {
      clearTimeout(this.connectTimer);
    }
  }

  mute(muted: boolean): void {
    if (this.state === 'closed' || this.state === 'finishing') return;
    this.muted = muted;
    for (const track of this.options.micStream.getAudioTracks())
      track.enabled = !muted;
    if (this.state === 'active') {
      try {
        this.send({
          type: muted
            ? 'session.input_audio.mute'
            : 'session.input_audio.unmute',
          event_id: liveVoiceEventId(),
        });
      } catch {
        this.fail('connection');
      }
    }
  }

  appendContext(content: string, kind: LiveVoiceContextKind): boolean {
    if (
      this.state === 'closed' ||
      this.state === 'finishing' ||
      !this.options.isCurrentCall()
    )
      return false;
    let chunks: string[];
    try {
      chunks = chunkLiveVoiceContext(content);
    } catch {
      this.fail('capacity');
      return false;
    }
    if (this.state !== 'active') {
      const bytes = new TextEncoder().encode(content).byteLength;
      if (this.queuedBytes + bytes > MAX_QUEUED_CONTEXT_BYTES) {
        this.fail('capacity');
        return false;
      }
      this.queuedBytes += bytes;
      this.queuedContext.push(
        ...chunks.map((chunk) => ({ content: chunk, kind }))
      );
      return true;
    }
    try {
      for (const chunk of chunks) this.sendContext(chunk, kind);
      return true;
    } catch {
      this.fail('connection');
      return false;
    }
  }

  interrupt(): void {
    if (this.state !== 'active' || !this.options.isCurrentCall()) return;
    this.playback?.interrupt();
    this.appendContext(
      'Stop the current speech and listen for the user’s next request.',
      'instructions'
    );
  }

  finish(): Promise<boolean> {
    void this.options.onTranscriptFlush?.();
    if (this.finishPromise) return this.finishPromise;
    if (this.state !== 'active') {
      this.close();
      return (
        this.options
          .onTranscriptFlush?.(this.finalized)
          .then((saved) => this.finalized && saved) ??
        Promise.resolve(this.finalized)
      );
    }
    this.state = 'finishing';
    this.calls.stop();
    this.stopMedia();
    this.finishPromise = new Promise((resolve) => {
      this.resolveFinished = resolve;
    });
    this.finishTimer = setTimeout(
      () => this.close(),
      LIVE_VOICE_FINISH_TIMEOUT_MS
    );
    try {
      this.send({ type: 'session.close', event_id: liveVoiceEventId() }, true);
    } catch {
      this.close();
    }
    return this.finishPromise;
  }

  close(): void {
    if (this.state === 'closed') return;
    const transcriptUpload = this.options.onTranscriptFlush?.(this.finalized);
    this.state = 'closed';
    this.calls.stop();
    this.abort.abort();
    clearTimeout(this.connectTimer);
    clearTimeout(this.finishTimer);
    clearTimeout(this.disconnectTimer);
    this.rejectStarted?.();
    if (transcriptUpload) {
      void transcriptUpload.then(
        (saved) => this.resolveFinished?.(this.finalized && saved),
        () => this.resolveFinished?.(false)
      );
    } else this.resolveFinished?.(this.finalized);
    this.queuedContext = [];
    this.queuedBytes = 0;
    this.contextCommands.clear();
    this.stopMedia();
    const cleanup = [() => this.channel?.close(), () => this.peer?.close()];
    for (const release of cleanup) {
      try {
        release();
      } catch {
        /* Continue releasing independent resources. */
      }
    }
  }

  private stopMedia(): void {
    if (this.mediaStopped) return;
    this.mediaStopped = true;
    const cleanup = [
      () => this.playback?.dispose(),
      () => this.inputActivity?.dispose(),
      ...this.options.micStream.getTracks().map((track) => () => track.stop()),
      ...(this.remoteStream?.getTracks() ?? []).map(
        (track) => () => track.stop()
      ),
      () => this.options.audioElement.pause(),
      () => {
        this.options.audioElement.srcObject = null;
      },
    ];
    for (const release of cleanup) {
      try {
        release();
      } catch {
        /* Continue releasing independent resources. */
      }
    }
    this.playback = null;
    this.inputActivity = null;
  }

  private assertCurrent(): void {
    if (this.abort.signal.aborted || !this.options.isCurrentCall()) {
      throw new Error('Live voice connection is no longer available');
    }
  }

  private fail(reason: LiveVoiceFailure): void {
    if (this.state === 'closed') return;
    this.close();
    this.options.onFailure?.(reason);
  }

  private send(event: LiveVoiceEvent, allowFinishing = false): void {
    const allowed =
      this.state === 'active' || (allowFinishing && this.state === 'finishing');
    if (!allowed || this.channel?.readyState !== 'open') {
      throw new Error('Live voice event channel is unavailable');
    }
    if (!allowFinishing) this.assertCurrent();
    this.channel.send(JSON.stringify(event));
  }

  private sendContext(content: string, kind: LiveVoiceContextKind): void {
    const eventId = liveVoiceEventId();
    this.send({
      type: `session.${kind}.append`,
      event_id: eventId,
      delegation_id: null,
      content,
    });
    this.contextCommands.remember(eventId, kind);
  }

  private receive(data: unknown): void {
    if (this.state === 'closed') return;
    // Final accounting must drain even after the UI relinquishes the call.
    if (this.state !== 'finishing' && !this.options.isCurrentCall())
      return this.close();
    const event = parseLiveVoiceEvent(data);
    if (!event) return this.fail('protocol');
    try {
      if (event.type === 'session.started') {
        const session = liveVoiceRecord(event.session);
        if (
          session?.id !== this.providerSessionId ||
          session?.model !== this.expectedModel
        ) {
          return this.fail('protocol');
        }
        if (this.state === 'connecting') {
          this.state = 'active';
          if (this.muted) this.mute(true);
          for (const item of this.queuedContext)
            this.sendContext(item.content, item.kind);
          this.queuedContext = [];
          this.queuedBytes = 0;
          this.resolveStarted?.();
        }
      }
      this.calls.observe(event);
      if (event.type === 'session.closed') {
        if (!isLiveVoiceFinalEvent(event, this.providerSessionId))
          return this.fail('protocol');
        this.finalized = true;
      }
      this.options.onEvent(event);
      if (event.type === 'session.closed') this.close();
    } catch {
      this.fail('protocol');
    }
  }

  private async openConnection(args: ConnectOptions): Promise<void> {
    const peer = new RTCPeerConnection();
    this.peer = peer;
    if (this.options.onInputActivity) {
      this.inputActivity = observeLiveVoiceInputActivity({
        stream: this.options.micStream,
        onInputActivity: this.options.onInputActivity,
      });
    }
    peer.addEventListener('track', (event) => this.receiveTrack(event));
    peer.addEventListener('connectionstatechange', () =>
      this.connectionChanged(peer)
    );
    for (const track of this.options.micStream.getAudioTracks()) {
      track.enabled = !this.muted;
      peer.addTrack(track, this.options.micStream);
    }
    const channel = peer.createDataChannel('oai-events');
    this.channel = channel;
    channel.addEventListener('message', (event) => this.receive(event.data));
    channel.addEventListener('error', () => this.fail('connection'));
    channel.addEventListener('close', () => this.fail('connection'));
    const offer = await peer.createOffer();
    this.assertCurrent();
    await peer.setLocalDescription(offer);
    this.assertCurrent();
    await this.waitForIce(peer);
    this.assertCurrent();
    const sdp = peer.localDescription?.sdp;
    if (!sdp) throw new Error('Live voice SDP offer is unavailable');
    const response = await fetch(args.url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${args.apiKey}`,
        'Content-Type': 'application/sdp',
      },
      body: sdp,
      signal: this.abort.signal,
    });
    this.assertCurrent();
    if (!response.ok) throw new Error('Live voice negotiation failed');
    const answer = parseLiveVoiceAnswer(await response.json());
    this.assertCurrent();
    if (!answer) throw new Error('Live voice SDP answer is invalid');
    this.providerSessionId = answer.sessionId;
    await peer.setRemoteDescription({ type: 'answer', sdp: answer.sdp });
    this.assertCurrent();
  }

  private waitForIce(peer: RTCPeerConnection): Promise<void> {
    if (peer.iceGatheringState === 'complete') return Promise.resolve();
    return new Promise((resolve, reject) => {
      const cleanup = () => {
        peer.removeEventListener('icegatheringstatechange', check);
        this.abort.signal.removeEventListener('abort', abort);
      };
      const check = () => {
        if (peer.iceGatheringState !== 'complete') return;
        cleanup();
        resolve();
      };
      const abort = () => {
        cleanup();
        reject(new Error('Live voice ICE gathering stopped'));
      };
      peer.addEventListener('icegatheringstatechange', check);
      this.abort.signal.addEventListener('abort', abort, { once: true });
      if (this.abort.signal.aborted) abort();
      else check();
    });
  }

  private receiveTrack(event: RTCTrackEvent): void {
    if (this.isClosing || !this.options.isCurrentCall()) return;
    const stream = event.streams[0] ?? new MediaStream([event.track]);
    this.remoteStream = stream;
    this.options.audioElement.srcObject = stream;
    this.playback?.dispose();
    this.playback = observeLiveVoicePlayback({
      stream,
      audioElement: this.options.audioElement,
      onPlaybackChange: this.options.onPlaybackChange,
    });
    this.options.onRemoteStream(stream);
    void this.options.audioElement.play().catch(() => {
      if (!this.isClosing && this.options.isCurrentCall())
        this.options.onFailure?.('playback');
    });
  }

  private connectionChanged(peer: RTCPeerConnection): void {
    if (this.state === 'closed') return;
    clearTimeout(this.disconnectTimer);
    if (
      peer.connectionState === 'failed' ||
      peer.connectionState === 'closed'
    ) {
      this.fail('connection');
    } else if (peer.connectionState === 'disconnected') {
      this.disconnectTimer = setTimeout(() => this.fail('connection'), 5_000);
    }
  }
}
